import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { toast } from 'sonner';
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import PageHeader from "../../../layout/shared/page-header";
import { RootState } from '../../../redux/store';
import { setSelectedView } from '../../../redux/userProfileSlice';
import { menuRoutes } from "../../../router/menu";
import { InvoiceService} from "../../../services/invoices/invoiceService";
import CustomSpinner from '../../../loader/CustomSpinner';
import {InvoiceApiResponse} from "../../../type/entities/invoice-type";

const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: menuRoutes.invoices.profile.info,
        permission: {
            group: 'invoice',
            action: 'get'
        }
    }
];

export default function InvoiceProfileLayout({ isLoading }: { isLoading?: boolean }) {

    // HOOKS

    const { selectedView } = useSelector((state: RootState) => state.invoiceProfile);
    const { id = '' } = useParams<{ id: string }>();
    const { handleErrors } = useHandleErrors();
    const navigate = useNavigate();
    const service = new InvoiceService();

    // FUNCTIONS

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES Obtener los datos de la factura
     *
     * @EN Get the invoice data
     */
    //--------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getInvoiceById(id);
        return response.getResponseData() as InvoiceApiResponse;
    }, [id]));
    //--------------------------------------------------------------------------------------------------------------

    const pageHeader = {
        title: `Información de la proforma ${data?.serialCode || 'Proforma'}`,
        breadcrumb: [
            { name: 'Administración' },
            { name: 'Proformas', href: menuRoutes.invoices.path },
            { name: 'Vista de proforma' },
        ],
    };

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES Manejar el borrado de la factura
     *
     * @EN Handle the invoice deletion
     */
    //--------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteInvoice(id)).getResponseData();
            if (response.success) {
                navigate(menuRoutes.clients.path);
                toast.success('Se ha eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        navigate(`${menuRoutes.invoices.path}/${id}/profile/${selectedView}`);
    }, [selectedView]);

    // RENDER

    return (
        <>
            <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb}>
                <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0'>
                </div>
            </PageHeader>

            <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />

            {isLoading ? <div className='m-auto h-full'><CustomSpinner /></div> : <Outlet />}
        </>
    );
}